import { defineComponent, reactive, ref, SetupContext } from '@vue/composition-api';
import SystemConst from '@/shared/system-const';
import { TableHeader, BulkAddUsersCheckResult, OnColumns, ConfigIconCoordinate } from '@/shared/type';
import vueRouter from '@/router/index';                                                     // ルート情報取得
import CommonOnColumnsMenu from '@/components/common/common-on-columns-menu.component.vue'; // 共通列設定メニューコンポーネント
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';  // メッセージダイアログコンポーネント
import CommonFunction from '@/shared/common-function';                                      // 共通関数定義
import MessageConst from '@/shared/message-const';                                          // 共通メッセージ定義
import HttpStatus from 'http-status-codes';                                                 // HTTPステータスコード取得用
import { DataOptions } from 'vuetify';                                                      // v-data-tableのupdateイベントの引数を利用するための型
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';      // フィルタ用共通コンポーネント
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    total: number;
    isLoading: boolean;
    isError: boolean;
    bulkAddUsersCheckResultItem: BulkAddUsersCheckResult[];
    bulkAddUsersCheckResultDisplayTableHeaders: TableHeader[];
    outputNormalResultsDialog: boolean;
    outputErrorResultsDialog: boolean;
    isDownLoading: boolean;
    displayConfirmationDialog: boolean;
    filterDefs: TableFilterDefs;
    pageOptions: DataOptions;
    filters: TableFilters;
};

/**
 * ユーザー一括登録(チェック)コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, CommonOnColumnsMenu, CommonTableFilter },
    setup(props, ctx: SetupContext) {
        const tableHeight = SystemConst.TABLE_HEIGHT.BULK_ADD_USERS;                  // テーブル高さ調整用変数
        const router = vueRouter;                                                     // ルータ
        const page = ref<number>(1);                                                  // ページ番号
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                         // 1ページあたりの行数
        const itemsPerPage = ref<number>(linesPerPage[1]);                            // 1ページあたりの行数（初期値）
        const sortBy = ref<string[]>([]);                                             // ソートカラム
        const sortDesc = ref<boolean[]>([]);                                          // ソート昇順、降順
        const tableHeaderClass: string[] = ['secondary subtitle-2'];                  // ユーザー一括登録チェック結果のヘッダ色
        const child = ref<any>();                                                     // テーブルフィルタ一覧
        const commonRef = ref<any>(null);                                             // メッセージダイアログ
        const columnRef = ref<any>(null);                                             // 列設定    
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                      // テーブルのデータ取得中に表示するテキスト
        const stepper = <number>2;                                                    // ステッパーの2段階目
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },
            total: 0,                                                                 // ユーザー一括登録チェック結果の総件数
            isLoading: false,                                                         // テーブルがロード中: true ロード中でない: false
            isError: false,                                                           // errorContentsにエラー内容がある: true エラー内容がない: false
            bulkAddUsersCheckResultItem: [],                                          // ユーザー一括登録チェック結果一覧情報
            bulkAddUsersCheckResultDisplayTableHeaders: [],                           // 表示するテーブルヘッダー
            outputNormalResultsDialog: false,                                         // 正常データダウンロードダイアログ
            outputErrorResultsDialog: false,                                          // エラーデータダウンロードダイアログ
            isDownLoading: false,                                                     // ファイルがダウンロード中: true ダウンロード中でない: false
            displayConfirmationDialog: false,                                         // Excel出力確認ダイアログ表示フラグ
            filterDefs: {                                                             // フィルタ定義
                'lineNo': { title: '行番号', types: 'NumRange' },
                'processCode': { title: '処理フラグ', types: 'CheckBox' },
                'userId': { title: 'ユーザーID', types: 'KeyWordPartialMatch' },
                'identificationCode': { title: '識別コード', types: 'KeyWordPartialMatch' },
                'accountDivision': { title: 'アカウント区分', types: 'CheckBox' },
                'isOrganizationManager': { title: '組織管理者', types: 'CheckBox' },
                'lastName': { title: '姓', types: 'KeyWordPartialMatch' },
                'firstName': { title: '名', types: 'KeyWordPartialMatch' },
                'lastNameKana': { title: '姓（かな）', types: 'KeyWordPartialMatch' },
                'firstNameKana': { title: '名（かな）', types: 'KeyWordPartialMatch' },
                'organizationId': { title: '組織ID', types: 'KeyWordPartialMatch' },
                'mail': { title: 'メールアドレス', types: 'KeyWordPartialMatch' },
                'password': { title: 'パスワード', types: 'KeyWordPartialMatch' },
                'postName': { title: '職', types: 'KeyWordPartialMatch' },
                'fiscalYear': { title: '作成年度', types: 'PullDown' },
                'isEnabled': { title: '状態', types: 'CheckBox' },
                'errorContents': { title: 'エラー内容', types: 'KeyWordPartialMatch' }
            },
            pageOptions: {                                                            // ページ情報・ソート情報
                page: 1, itemsPerPage: linesPerPage[1], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            filters: {},                                                              // フィルタ情報
        });
        const errorIcon: TableHeader[] = [
            // エラーアイコンヘッダ
            {
                text: '',
                value: 'isError',
                width: '40px',
                class: tableHeaderClass,
                sortable: false
            }
        ];
        // チェック結果一覧ヘッダ情報(初期表示)
        const checkResultInitialDisplayTableHeaders: TableHeader[] = [
            // 行番号
            {
                text: '行番号',
                value: 'lineNo',
                width: '100px',
                class: tableHeaderClass,
            },
            // 処理フラグ
            {
                text: '処理フラグ',
                value: 'processCode',
                width: '140px',
                class: tableHeaderClass,
            },
            // 識別コード
            {
                text: '識別コード',
                value: 'identificationCode',
                width: '140px',
                class: tableHeaderClass,
            },
            // アカウント区分
            {
                text: 'アカウント区分',
                value: 'accountDivision',
                width: '190px',
                class: tableHeaderClass,
            },
            // 姓
            {
                text: '姓',
                value: 'lastName',
                width: '100px',
                class: tableHeaderClass,
            },
            // 名
            {
                text: '名',
                value: 'firstName',
                width: '100px',
                class: tableHeaderClass,
            },
            // 組織ID
            {
                text: '組織ID',
                value: 'organizationId',
                width: '140px',
                class: tableHeaderClass,
            },
            // メールアドレス
            {
                text: 'メールアドレス',
                value: 'mail',
                width: '290px',
                class: tableHeaderClass,
            },
            // エラー内容
            {
                text: 'エラー内容',
                value: 'errorContents',
                width: '400px',
                class: tableHeaderClass,
            },
            // 行メニュー
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];
        // チェック結果一覧ヘッダ情報(全項目)
        const checkResultTableHeaders: TableHeader[] = [
            // 行番号
            {
                text: '行番号',
                value: 'lineNo',
                width: '100px',
                class: tableHeaderClass,
            },
            // 処理フラグ
            {
                text: '処理フラグ',
                value: 'processCode',
                width: '140px',
                class: tableHeaderClass,
            },
            // ユーザーID
            {
                text: 'ユーザーID',
                value: 'userId',
                width: '140px',
                class: tableHeaderClass,
            },
            // 識別コード
            {
                text: '識別コード',
                value: 'identificationCode',
                width: '140px',
                class: tableHeaderClass,
            },
            // アカウント区分
            {
                text: 'アカウント区分',
                value: 'accountDivision',
                width: '190px',
                class: tableHeaderClass,
            },
            // 組織管理者
            {
                text: '組織管理者',
                value: 'isOrganizationManager',
                width: '140px',
                class: tableHeaderClass,
            },
            // 姓
            {
                text: '姓',
                value: 'lastName',
                width: '100px',
                class: tableHeaderClass,
            },
            // 名
            {
                text: '名',
                value: 'firstName',
                width: '100px',
                class: tableHeaderClass,
            },
            // 姓（かな）
            {
                text: '姓（かな）',
                value: 'lastNameKana',
                width: '140px',
                class: tableHeaderClass,
            },
            // 名（かな）
            {
                text: '名（かな）',
                value: 'firstNameKana',
                width: '140px',
                class: tableHeaderClass,
            },
            // 組織ID
            {
                text: '組織ID',
                value: 'organizationId',
                width: '140px',
                class: tableHeaderClass,
            },
            // メールアドレス
            {
                text: 'メールアドレス',
                value: 'mail',
                width: '290px',
                class: tableHeaderClass,
            },
            // パスワード
            {
                text: 'パスワード',
                value: 'password',
                width: '180px',
                class: tableHeaderClass,
            },
            // 職
            {
                text: '職',
                value: 'postName',
                width: '110px',
                class: tableHeaderClass
            },
            // 作成年度
            {
                text: '作成年度',
                value: 'fiscalYear',
                width: '110px',
                class: tableHeaderClass
            },
            // 状態
            {
                text: '状態',
                value: 'isEnabled',
                width: '110px',
                class: tableHeaderClass
            },
            // エラー内容
            {
                text: 'エラー内容',
                value: 'errorContents',
                width: '400px',
                class: tableHeaderClass,
            },
            // 行メニュー
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];

        // 列設定情報
        const onColumns: OnColumns[] = [
            {
                text: '全て表示',
                value: false
            },
            {
                text: '行番号',
                value: true
            },
            {
                text: '処理フラグ',
                value: true
            },
            {
                text: 'ユーザーID',
                value: false
            },
            {
                text: '識別コード',
                value: true
            },
            {
                text: 'アカウント区分',
                value: true
            },
            {
                text: '組織管理者',
                value: false
            },
            {
                text: '姓',
                value: true
            },
            {
                text: '名',
                value: true
            },
            {
                text: '姓（かな）',
                value: false
            },
            {
                text: '名（かな）',
                value: false
            },
            {
                text: '組織ID',
                value: true
            },
            {
                text: 'メールアドレス',
                value: true
            },
            {
                text: 'パスワード',
                value: false
            },
            {
                text: '職',
                value: false
            },
            {
                text: '作成年度',
                value: false
            },
            {
                text: '状態',
                value: false
            },
            {
                text: 'エラー内容',
                value: true
            },
        ];

        // フィルタのバリデーション
        // 行番号
        const lineNoRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['numRangeLength'], '行番号')
        ];
        // ユーザーID
        const userIdRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], 'ユーザーID')
        ];
        // 識別コード
        const identificationCodeRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '識別コード')
        ];
        // 姓
        const lastNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '姓')
        ];
        // 名
        const firstNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '名')
        ];
        // 姓（かな）
        const lastNameKanaRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '姓（かな）')
        ];
        // 名（かな）
        const firstNameKanaRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '名（かな）')
        ];
        // 組織ID
        const organizationIdRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '組織ID')
        ];
        // メールアドレス
        const mailRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], 'メールアドレス')
        ];
        // パスワード
        const passwordRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], 'パスワード')
        ];
        // 職
        const postNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '職')
        ];
        // エラー内容
        const errorContentsRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], 'エラー内容')
        ];
        // フィルターに設定された最大桁数
        const maxLength = {
            textLength: SystemConst.FILTER_MAX_LENGTH.FILTER_LENGTH,
            numRangeLength: SystemConst.FILTER_MAX_LENGTH.NUM_RANGE_LENGTH
        }

        /**
         * created
         */
        created();

        /**
         * ユーザー一括登録チェック結果画面初期表示処理
         */
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-file-upload-outline');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', 'ユーザー一括登録');

            // 初期表示ヘッダ設定
            state.bulkAddUsersCheckResultDisplayTableHeaders = errorIcon.concat(checkResultInitialDisplayTableHeaders);

            // フィルタの初期設定を行う
            // 初期設定が完了するまでawaitで待つ
            await initFilter();

            // ユーザー一括登録チェック結果情報取得
            getResults();
        }

        /**
         * フィルタ初期設定
         * 
         * フィルタにDBから取得する必要のある条件がある場合、初期表示で取得する。
         */
        async function initFilter() {
            // フィルタ条件の内容を取得
            // ユーザー一括登録管理ストアのgetFilterConditionsを呼び出す
            const response = await ctx.root.$store.dispatch('bulkAddUsers/getFilterConditions');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // フィルタ（プルダウン・チェックボックス）に表示するデータを設定する
            // 処理フラグ
            state.filterDefs['processCode'].chkItems = response.data.processCode;
            // アカウント区分
            state.filterDefs['accountDivision'].chkItems = response.data.accountDivision;
            // 組織管理者
            state.filterDefs['isOrganizationManager'].chkItems = response.data.isOrganizationManager;
            // 作成年度
            state.filterDefs['fiscalYear'].items = response.data.fiscalYear;
            // 状態
            state.filterDefs['isEnabled'].chkItems = response.data.isEnabled;

            // バリデーションを設定する
            // 行番号
            state.filterDefs['lineNo'].startValidator = lineNoRules;
            state.filterDefs['lineNo'].endValidator = lineNoRules;
            // ユーザーID
            state.filterDefs['userId'].validator = userIdRules;
            // 識別コード
            state.filterDefs['identificationCode'].validator = identificationCodeRules;
            // 姓
            state.filterDefs['lastName'].validator = lastNameRules;
            // 名
            state.filterDefs['firstName'].validator = firstNameRules;
            // 姓（かな）
            state.filterDefs['lastNameKana'].validator = lastNameKanaRules;
            // 名（かな）
            state.filterDefs['firstNameKana'].validator = firstNameKanaRules;
            // 組織ID
            state.filterDefs['organizationId'].validator = organizationIdRules;
            // メールアドレス
            state.filterDefs['mail'].validator = mailRules;
            // パスワード
            state.filterDefs['password'].validator = passwordRules;
            // 職
            state.filterDefs['postName'].validator = postNameRules;
            // エラー内容
            state.filterDefs['errorContents'].validator = errorContentsRules;
        }

        /**
         * フィルタ設定によるイベント
         * 
         * フィルタの条件を追加・削除したタイミングで呼ばれる。
         * このイベントでv-data-tableの表示更新を行うこと。
         * 
         * @param filters フィルタ条件
         */
        function updateFilter(filters: TableFilters) {
            // フィルタ条件をstate内へ保存する
            state.filters = filters;

            // 1ページへ戻す
            state.pageOptions.page = 1;
            page.value = 1;

            // ユーザー一括登録チェック結果情報取得
            getResults();
        }

        /**
         * 一覧のページ／ソート操作によるイベント
         * 
         * @param options ページ／ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ/ソート情報をstate内へ保存する
            state.pageOptions = options;

            if (page.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                // データの中身を比較して、いずれも変更されていなかった場合、
                // ページの更新を行わない
                return;
            }

            // ページ情報を設定する
            page.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソート情報を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // ユーザー一括登録チェック結果情報取得
            getResults();
        }

        /**
         * ユーザー一括登録チェック結果取得処理
         */
        async function getResults() {
            // ローディングのフラグをtrueにする
            state.isLoading = true;

            // ユーザー一括登録チェック結果情報をクリア
            state.bulkAddUsersCheckResultItem = [];

            // 総データ件数をクリア
            state.total = 0;

            // ページ情報・ソート情報、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // ユーザー一括登録チェック結果情報取得APIを呼び出す
            // パラメータ：作成したパラメータ
            const response = await ctx.root.$store.dispatch('bulkAddUsers/getResults',
                {
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams']
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する。
                state.isLoading = false;

                // HTTPステータスコードが404の場合、
                // エラーメッセージを表示してファイル選択画面へ遷移する。
                if (response.status === HttpStatus.NOT_FOUND) {
                    // 404エラーのみダイアログを表示する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/bulk-add-users/upload');
                } else {
                    // HTTPステータスコードが404以外はエラーダイアログを表示、又はエラー画面へ遷移する。
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // ユーザー一括登録チェック結果情報の更新を行う
            state.bulkAddUsersCheckResultItem = response.data.checkResults;

            // 総データ件数の更新を行う
            state.total = response.data.total;

            // エラー有無を取得する
            // エラー内容が存在する場合、登録ボタンを非表示にするため、state.isErrorをtrueにする
            state.isError = response.data.isError;

            // ローディングのフラグをfalseにする
            state.isLoading = false;
        }

        /**
         * 登録ボタン押下時、ダイアログ表示処理
         */
        function displayDialog() {
            // 確認メッセージを設定する
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['登録']);

            // 確認メッセージダイアログ表示
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
            // yes をクリックするとbulkRegistration()を呼び出す
            // no をクリックするとダイアログを閉じる
        }

        /**
         * 確認画面内容登録処理
         */
        async function bulkRegistration() {
            // 登録処理のAPIを実行する
            const response = await ctx.root.$store.dispatch('bulkAddUsers/bulkRegistration');

            // ステータスコード200以外の場合
            if (response.status !== HttpStatus.OK) {
                // ステータスコード400の場合
                if (response.status === HttpStatus.BAD_REQUEST) {
                    // 追加されたエラーを確認する
                    getResults();
                } else if (response.status === HttpStatus.NOT_FOUND) {
                    // 404エラーのダイアログを表示する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/bulk-add-users/upload');
                    return;
                }
                // エラーメッセージダイアログを表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }
            // ステータスコード200の場合
            // 画面遷移処理を行う
            registrationConfirmation();
        }

        /**
         * ユーザー一括登録チェック結果Excel出力処理
         * 
         * @param isNormalData 正常データのExcel出力か（true:1, false:0）
         */
        async function outputResults(isNormalData: number) {
            // 確認ダイアログを非表示にする
            state.displayConfirmationDialog = false;

            // ファイルダウンロード中にローダーを表示する
            state.isDownLoading = true;

            // Excelファイル名初期化
            let fileName = '';

            // Excelファイル名格納
            if (isNormalData === 1) {
                // 正常データの場合
                fileName = SystemConst.EXCEL_API_PARAM.BULK_ADD_USERS_NORMAL_RESULTS_FILE;
            } else {
                // エラーデータの場合
                fileName = SystemConst.EXCEL_API_PARAM.BULK_ADD_USERS_ERROR_RESULTS_FILE;
            }

            // ページ情報・ソート情報、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 一括登録storeのoutputResultsを呼び出す
            // パラメータ:フィルタ・ソート情報
            //            Excelファイル名
            //            正常データのExcel出力か
            const response = await ctx.root.$store.dispatch('bulkAddUsers/outputResults',
                {
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams'],
                    fileName,
                    isNormalData
                }
            );

            // ファイルダウンロード中表示するローダーを非表示にする
            state.isDownLoading = false;

            // Excel出力ダイアログの表示をfalseにする
            if (isNormalData === 1) {
                // 正常データの場合
                state.outputNormalResultsDialog = false;
            } else {
                // エラーデータの場合
                state.outputErrorResultsDialog = false;
            }


            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }
        }

        /**
         * 戻るボタン押下処理
         * ファイル選択画面へ戻る
         */
        function back(): void {
            router.push('/bulk-add-users/upload');
        }

        /**
         * 登録成功時の完了画面遷移処理
         */
        function registrationConfirmation() {
            router.push('/bulk-add-users/complete');
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        /**
         * 列設定アイコンをクリックした際に発火する
         * 列設定アイコンの座標を取得し、列設定メニューコンポーネントのshowMenuを発火する
         */
        function showMenu() {
            // 列設定アイコンの座標を取得
            const configIconCoordinate: ConfigIconCoordinate = { x: null, y: null };
            configIconCoordinate.x = document.querySelector('#column');
            configIconCoordinate.y = document.querySelector('#column');

            // 列設定メニューコンポーネントのshowMenuを発火
            columnRef.value.showMenu(configIconCoordinate);
        }

        /**
         * 列設定メニューで、列設定情報が更新された際に発火する
         * 設定後のテーブルヘッダー情報を取得する
         * 
         * @param displayTableHeaders 画面に表示するテーブルヘッダー情報
         */
        function getTableHeader(displayTableHeaders: TableHeader[]) {
            // ヘッダー情報の更新
            state.bulkAddUsersCheckResultDisplayTableHeaders = errorIcon.concat(displayTableHeaders);
        }

        return {
            tableHeight,
            stepper,
            outputExcelDialog: false,
            state,
            page,
            linesPerPage,
            itemsPerPage,
            sortBy,
            sortDesc,
            child,
            commonRef,
            columnRef,
            tableLoadingText,
            checkResultInitialDisplayTableHeaders,
            checkResultTableHeaders,
            onColumns,
            updateFilter,
            updateOptions,
            back,
            displayDialog,
            bulkRegistration,
            onResize,
            showMenu,
            getTableHeader,
            outputResults
        }
    }
});
